import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import RoomGrid from './RoomGrid';

const getTodayValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createDateFromValue = (dateValue) => {
  if (!dateValue) return null;
  return new Date(`${dateValue}T12:00:00`);
};

const formatDate = (value) => {
  const date = typeof value === 'string' ? createDateFromValue(value) : value;

  if (!date || Number.isNaN(date.getTime())) return 'No date';

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getWeekDetails = (dateValue) => {
  const selectedDate = createDateFromValue(dateValue);

  if (!selectedDate) {
    return {
      weekStart: '',
      weekEnd: '',
      title: 'H+S Check'
    };
  }

  const weekStart = new Date(selectedDate);
  const dayOfWeek = weekStart.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  weekStart.setDate(weekStart.getDate() + mondayOffset);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const weekStartValue = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
  const weekEndValue = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}-${String(weekEnd.getDate()).padStart(2, '0')}`;

  return {
    weekStart: weekStartValue,
    weekEnd: weekEndValue,
    title: `H+S Check - Week Commencing ${formatDate(weekStart)}`
  };
};

const getCreatedAtValue = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return new Date(value).getTime();
  if (value.seconds) return value.seconds * 1000;
  return new Date(value).getTime();
};

const buildManagerReports = (roomChecks = []) => {
  const reportMap = roomChecks.reduce((accumulator, roomCheck) => {
    const issues = roomCheck.issues || [];

    if (!issues.length) return accumulator;

    const reportKey = roomCheck.managerInitials || `unassigned-${roomCheck.roomId}`;

    if (!accumulator[reportKey]) {
      accumulator[reportKey] = {
        managerInitials: roomCheck.managerInitials || '',
        managerName: roomCheck.managerName || 'Unassigned Room',
        issues: []
      };
    }

    issues.forEach((issue) => {
      accumulator[reportKey].issues.push({
        issueId: issue.id,
        label: issue.label,
        notes: issue.notes || '',
        cells: issue.cells || [],
        status: issue.status || 'open',
        identifiedAt: issue.identifiedAt || '',
        signedOffAt: issue.signedOffAt || '',
        signedOffByInitials: issue.signedOffByInitials || '',
        signedOffByName: issue.signedOffByName || '',
        roomId: roomCheck.roomId,
        roomName: roomCheck.roomName
      });
    });

    return accumulator;
  }, {});

  return Object.values(reportMap)
    .map((report) => {
      const openCount = report.issues.filter((issue) => issue.status !== 'sorted').length;
      const sortedCount = report.issues.length - openCount;

      return {
        ...report,
        openCount,
        sortedCount,
        signedOff: openCount === 0 && report.issues.length > 0
      };
    })
    .sort((left, right) => left.managerName.localeCompare(right.managerName));
};

const getRoomIssueStyles = (issues = []) => issues.map((issue) => ({
  ...issue,
  color: issue.status === 'sorted' ? '#bbf7d0' : '#fecaca',
  overlayBackground: issue.status === 'sorted' ? 'rgba(220, 252, 231, 0.92)' : 'rgba(254, 242, 242, 0.92)',
  borderColor: issue.status === 'sorted' ? '#16a34a' : '#dc2626'
}));

function HealthSafetyWalkRound({ db, currentUserInitials, currentUserDisplayName, currentUserRole }) {
  const [rooms, setRooms] = useState([]);
  const [checks, setChecks] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [selectedCells, setSelectedCells] = useState([]);
  const [issueLabel, setIssueLabel] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [draftIssuesByRoom, setDraftIssuesByRoom] = useState({});
  const [checkDate, setCheckDate] = useState(getTodayValue());
  const [statusMessage, setStatusMessage] = useState('');
  const [isSavingCheck, setIsSavingCheck] = useState(false);
  const [signingIssueId, setSigningIssueId] = useState('');
  const [removingIssueId, setRemovingIssueId] = useState('');

  useEffect(() => {
    const roomsQuery = query(collection(db, 'rooms'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(roomsQuery, (snapshot) => {
      const nextRooms = snapshot.docs.map((roomDoc) => ({ id: roomDoc.id, ...roomDoc.data() }));
      setRooms(nextRooms);
    });

    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    const checksQuery = query(collection(db, 'hs_checks'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(checksQuery, (snapshot) => {
      const nextChecks = snapshot.docs.map((checkDoc) => ({ id: checkDoc.id, ...checkDoc.data() }));
      setChecks(nextChecks);
    });

    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    if (!rooms.length) {
      setCurrentRoomIndex(0);
      return;
    }

    if (currentRoomIndex > rooms.length - 1) {
      setCurrentRoomIndex(rooms.length - 1);
    }
  }, [currentRoomIndex, rooms.length]);

  const weekDetails = useMemo(() => getWeekDetails(checkDate), [checkDate]);
  const currentRoom = rooms[currentRoomIndex] || null;
  const currentRoomIssues = currentRoom ? (draftIssuesByRoom[currentRoom.id] || []) : [];
  const totalDraftIssues = useMemo(
    () => Object.values(draftIssuesByRoom).reduce((total, roomIssues) => total + roomIssues.length, 0),
    [draftIssuesByRoom]
  );
  const sortedChecks = useMemo(
    () => [...checks].sort((left, right) => getCreatedAtValue(right.createdAt) - getCreatedAtValue(left.createdAt)),
    [checks]
  );
  const canManageAnyReport = ['Admin', 'Super Admin', 'Planner'].includes(currentUserRole);

  const handleCellClick = (cellKey) => {
    setSelectedCells((previous) => (
      previous.includes(cellKey)
        ? previous.filter((existingKey) => existingKey !== cellKey)
        : [...previous, cellKey]
    ));
  };

  const handleAddIssue = () => {
    if (!currentRoom) {
      setStatusMessage('Create at least one room before starting a walk round.');
      return;
    }

    if (!issueLabel.trim()) {
      setStatusMessage('Enter an issue label first.');
      return;
    }

    if (!selectedCells.length) {
      setStatusMessage('Select one or more squares for the issue.');
      return;
    }

    const selectedLookup = selectedCells.reduce((accumulator, cellKey) => {
      accumulator[cellKey] = true;
      return accumulator;
    }, {});

    setDraftIssuesByRoom((previous) => {
      const existingIssues = previous[currentRoom.id] || [];
      const filteredIssues = existingIssues
        .map((issue) => ({
          ...issue,
          cells: (issue.cells || []).filter((cellKey) => !selectedLookup[cellKey])
        }))
        .filter((issue) => issue.cells.length > 0);

      return {
        ...previous,
        [currentRoom.id]: [
          ...filteredIssues,
          {
            id: `issue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            label: issueLabel.trim(),
            notes: issueNotes.trim(),
            cells: [...selectedCells].sort(),
            status: 'open',
            identifiedAt: new Date().toISOString()
          }
        ]
      };
    });

    setIssueLabel('');
    setIssueNotes('');
    setSelectedCells([]);
    setStatusMessage('Issue added to the current walk round.');
  };

  const handleDeleteDraftIssue = (roomId, issueId) => {
    setDraftIssuesByRoom((previous) => ({
      ...previous,
      [roomId]: (previous[roomId] || []).filter((issue) => issue.id !== issueId)
    }));
    setStatusMessage('Issue removed from the current walk round.');
  };

  const handleSaveCheck = async () => {
    if (!rooms.length) {
      setStatusMessage('Create at least one room before saving a walk round.');
      return;
    }

    if (!checkDate) {
      setStatusMessage('Select the check date first.');
      return;
    }

    setIsSavingCheck(true);

    try {
      const roomChecks = rooms.map((room, index) => ({
        roomId: room.id,
        roomName: room.name || `Room ${index + 1}`,
        roomOrder: index + 1,
        width: Number(room.width) || 0,
        length: Number(room.length) || 0,
        managerInitials: room.managerInitials || '',
        managerName: room.managerName || '',
        items: room.items || [],
        roomCreatedAt: room.createdAt || null,
        issues: (draftIssuesByRoom[room.id] || []).map((issue) => ({
          ...issue,
          status: issue.status || 'open'
        }))
      }));
      const managerReports = buildManagerReports(roomChecks);

      await addDoc(collection(db, 'hs_checks'), {
        title: weekDetails.title,
        checkDate,
        weekStart: weekDetails.weekStart,
        weekEnd: weekDetails.weekEnd,
        totalRooms: roomChecks.length,
        totalIssues: roomChecks.reduce((total, roomCheck) => total + (roomCheck.issues || []).length, 0),
        roomChecks,
        managerReports,
        createdAt: new Date(),
        createdByInitials: currentUserInitials || '',
        createdByName: currentUserDisplayName || currentUserInitials || ''
      });

      setDraftIssuesByRoom({});
      setSelectedCells([]);
      setIssueLabel('');
      setIssueNotes('');
      setCurrentRoomIndex(0);
      setStatusMessage('H+S walk round saved.');
    } catch (error) {
      console.error('Error saving H+S walk round:', error);
      setStatusMessage('Failed to save the H+S walk round.');
    } finally {
      setIsSavingCheck(false);
    }
  };

  const handleSignOffIssue = async (check, issueId, reportManagerInitials) => {
    const canSign = canManageAnyReport || (reportManagerInitials && reportManagerInitials === currentUserInitials);

    if (!canSign) {
      setStatusMessage('Only the responsible manager can sign off this issue.');
      return;
    }

    setSigningIssueId(issueId);

    try {
      const updatedRoomChecks = (check.roomChecks || []).map((roomCheck) => ({
        ...roomCheck,
        issues: (roomCheck.issues || []).map((issue) => (
          issue.id !== issueId
            ? issue
            : {
                ...issue,
                status: 'sorted',
                signedOffAt: new Date().toISOString(),
                signedOffByInitials: currentUserInitials || '',
                signedOffByName: currentUserDisplayName || currentUserInitials || ''
              }
        ))
      }));
      const updatedManagerReports = buildManagerReports(updatedRoomChecks);

      await updateDoc(doc(db, 'hs_checks', check.id), {
        roomChecks: updatedRoomChecks,
        managerReports: updatedManagerReports,
        totalIssues: updatedRoomChecks.reduce((total, roomCheck) => total + (roomCheck.issues || []).length, 0),
        updatedAt: new Date()
      });

      setStatusMessage('Issue signed off as sorted.');
    } catch (error) {
      console.error('Error signing off H+S issue:', error);
      setStatusMessage('Failed to sign off issue.');
    } finally {
      setSigningIssueId('');
    }
  };

  const handleRemoveSavedIssue = async (check, issueId, reportManagerInitials) => {
    const canRemove = canManageAnyReport || (reportManagerInitials && reportManagerInitials === currentUserInitials);

    if (!canRemove) {
      setStatusMessage('Only the responsible manager can remove this issue.');
      return;
    }

    setRemovingIssueId(issueId);

    try {
      const updatedRoomChecks = (check.roomChecks || [])
        .map((roomCheck) => ({
          ...roomCheck,
          issues: (roomCheck.issues || []).filter((issue) => issue.id !== issueId)
        }))
        .filter((roomCheck) => roomCheck.issues.length > 0 || (roomCheck.items || []).length >= 0);
      const updatedManagerReports = buildManagerReports(updatedRoomChecks);

      await updateDoc(doc(db, 'hs_checks', check.id), {
        roomChecks: updatedRoomChecks,
        managerReports: updatedManagerReports,
        totalIssues: updatedRoomChecks.reduce((total, roomCheck) => total + (roomCheck.issues || []).length, 0),
        updatedAt: new Date()
      });

      setStatusMessage('Issue removed from the saved H+S check.');
    } catch (error) {
      console.error('Error removing H+S issue:', error);
      setStatusMessage('Failed to remove issue.');
    } finally {
      setRemovingIssueId('');
    }
  };

  const handleDeleteSavedCheck = async (checkId) => {
    if (!window.confirm('Delete this saved walk round? This cannot be undone.')) {
      return;
    }

    setStatusMessage('Deleting saved walk round...');

    try {
      await deleteDoc(doc(db, 'hs_checks', checkId));
      setStatusMessage('Saved walk round deleted.');
    } catch (error) {
      console.error('Error deleting saved walk round:', error);
      setStatusMessage('Failed to delete saved walk round.');
    }
  };

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      <div className="card" style={{ margin: 0 }}>
        <div className="flex-between" style={{ gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Health &amp; Safety Walk Round Check</h3>
            <p className="text-sm" style={{ margin: 0 }}>
              Work through rooms in creation order, mark any problem squares, then save the full week check in one go.
            </p>
          </div>
          <div style={{ minWidth: '240px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Check Date</label>
            <input type="date" value={checkDate} onChange={(event) => setCheckDate(event.target.value)} />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>{weekDetails.title}</div>
          </div>
        </div>
        {statusMessage && (
          <div style={{ marginTop: '14px', fontSize: '12px', fontWeight: 'bold', color: statusMessage.includes('Failed') ? 'var(--danger)' : 'var(--success)' }}>
            {statusMessage}
          </div>
        )}
      </div>

      {rooms.length === 0 ? (
        <div className="card" style={{ margin: 0 }}>
          <p className="text-sm" style={{ margin: 0 }}>No saved rooms yet. Add rooms in Create Room before starting a walk round.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(280px, 360px)', gap: '20px', alignItems: 'start' }}>
          <div className="card" style={{ margin: 0 }}>
            <div className="flex-between" style={{ gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>{currentRoom?.name}</h3>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Room {currentRoomIndex + 1} of {rooms.length}
                  {currentRoom?.managerName ? ` • Manager: ${currentRoom.managerName}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentRoomIndex((previous) => Math.max(previous - 1, 0));
                    setSelectedCells([]);
                  }}
                  disabled={currentRoomIndex === 0}
                  style={{ background: 'var(--light)', color: 'var(--text-main)' }}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentRoomIndex((previous) => Math.min(previous + 1, rooms.length - 1));
                    setSelectedCells([]);
                  }}
                  disabled={currentRoomIndex === rooms.length - 1}
                  style={{ background: 'var(--primary)', color: 'white' }}
                >
                  Next
                </button>
              </div>
            </div>

            {currentRoom && (
              <RoomGrid
                width={Number(currentRoom.width) || 0}
                length={Number(currentRoom.length) || 0}
                items={currentRoom.items || []}
                issueGroups={getRoomIssueStyles(currentRoomIssues)}
                selectedCells={selectedCells}
                onCellClick={handleCellClick}
                caption={`Walk round: ${currentRoom.name}`}
              />
            )}

            <div style={{ display: 'grid', gap: '12px', marginTop: '18px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Issue Found</label>
                <input value={issueLabel} onChange={(event) => setIssueLabel(event.target.value)} placeholder="e.g. Broken chair leg" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Issue Notes</label>
                <textarea
                  value={issueNotes}
                  onChange={(event) => setIssueNotes(event.target.value)}
                  placeholder="Optional notes for the manager report"
                  rows={3}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button type="button" onClick={handleAddIssue} style={{ background: 'var(--primary)', color: 'white' }}>
                  Save Issue To Room
                </button>
                <button type="button" onClick={() => setSelectedCells([])} style={{ background: 'var(--light)', color: 'var(--text-main)' }}>
                  Clear Selection
                </button>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Click one or more squares to highlight the affected area, then save the issue against this room.
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '20px' }}>
            <div className="card" style={{ margin: 0 }}>
              <div className="flex-between" style={{ marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>Room Issue List</h3>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{currentRoomIssues.length} issues in this room</span>
              </div>
              {currentRoomIssues.length === 0 ? (
                <p className="text-sm" style={{ margin: 0 }}>No issues logged for this room in the current walk round.</p>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {currentRoomIssues.map((issue) => (
                    <div key={issue.id} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px', background:'rgba(217,119,6,0.12)' }}>
                      <div className="flex-between" style={{ gap: '10px', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{issue.label}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{issue.cells.length} squares selected</div>
                          {issue.notes && <div style={{ fontSize: '12px', marginTop: '6px' }}>{issue.notes}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteDraftIssue(currentRoom.id, issue.id)}
                          style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: '11px', padding: '4px 8px' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card" style={{ margin: 0 }}>
              <div className="flex-between" style={{ gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '6px' }}>Save Weekly Check</h3>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{rooms.length} rooms included • {totalDraftIssues} issues logged</div>
                </div>
                <button type="button" onClick={handleSaveCheck} disabled={isSavingCheck} style={{ background: 'var(--success)', color: 'white' }}>
                  {isSavingCheck ? 'Saving...' : 'Save H+S Check'}
                </button>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {rooms.map((room, index) => {
                  const issueCount = (draftIssuesByRoom[room.id] || []).length;

                  return (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => {
                        setCurrentRoomIndex(index);
                        setSelectedCells([]);
                      }}
                      className={index === currentRoomIndex ? 'active-btn' : ''}
                      style={index === currentRoomIndex ? {} : { background: 'var(--light)', color: 'var(--text-main)' }}
                    >
                      {index + 1}. {room.name} {issueCount > 0 ? `(${issueCount})` : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ margin: 0 }}>
        <div className="flex-between" style={{ marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0 }}>Manager Reports &amp; Sign Off</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Each saved check groups issues by responsible manager and tracks sign-off once sorted.
            </div>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{sortedChecks.length} saved checks</span>
        </div>

        {sortedChecks.length === 0 ? (
          <p className="text-sm" style={{ margin: 0 }}>No H+S checks saved yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {sortedChecks.map((check) => (
              <div key={check.id} style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', background:'var(--light)' }}>
                <div className="flex-between" style={{ gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{check.title || 'H+S Check'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Check date: {formatDate(check.checkDate)}
                      {check.createdByName ? ` • Saved by ${check.createdByName}` : ''}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {check.totalRooms || 0} rooms • {check.totalIssues || 0} issues
                    </div>
                  </div>
                  {(canManageAnyReport || check.createdByInitials === currentUserInitials) && (
                    <button
                      type="button"
                      onClick={() => handleDeleteSavedCheck(check.id)}
                      style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: '12px', padding: '8px 10px' }}
                    >
                      Delete Walk Round
                    </button>
                  )}
                </div>

                {(check.managerReports || []).length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No issues were recorded in this check.</div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {(check.managerReports || []).map((report) => {
                      const canSignThisReport = canManageAnyReport || (report.managerInitials && report.managerInitials === currentUserInitials);

                      return (
                        <div key={`${check.id}-${report.managerInitials || report.managerName}`} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', background: report.signedOff ? 'rgba(27,143,90,0.12)' : 'rgba(217,119,6,0.12)' }}>
                          <div className="flex-between" style={{ gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                            <div>
                              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{report.managerName}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                {report.openCount} open • {report.sortedCount} sorted
                              </div>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: report.signedOff ? 'var(--success)' : 'var(--warning)' }}>
                              {report.signedOff ? 'All issues signed off' : 'Awaiting sign-off'}
                            </div>
                          </div>

                          <div style={{ display: 'grid', gap: '10px' }}>
                            {report.issues.map((issue) => (
                              <div key={issue.issueId} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px', background:'var(--light)' }}>
                                <div className="flex-between" style={{ gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                  <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{issue.label}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                      {issue.roomName} • {issue.cells.length} squares
                                    </div>
                                    {issue.notes && <div style={{ fontSize: '12px', marginTop: '6px' }}>{issue.notes}</div>}
                                    {issue.status === 'sorted' && issue.signedOffByName && (
                                      <div style={{ fontSize: '12px', color: 'var(--success)', marginTop: '6px' }}>
                                        Signed off by {issue.signedOffByName} on {formatDate(issue.signedOffAt)}
                                      </div>
                                    )}
                                  </div>
                                  {canSignThisReport && (
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      {issue.status !== 'sorted' && (
                                        <button
                                          type="button"
                                          onClick={() => handleSignOffIssue(check, issue.issueId, report.managerInitials)}
                                          disabled={signingIssueId === issue.issueId}
                                          style={{ background: 'var(--success)', color: 'white' }}
                                        >
                                          {signingIssueId === issue.issueId ? 'Signing Off...' : 'Mark Sorted'}
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveSavedIssue(check, issue.issueId, report.managerInitials)}
                                        disabled={removingIssueId === issue.issueId}
                                        style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)' }}
                                      >
                                        {removingIssueId === issue.issueId ? 'Removing...' : 'Remove Issue'}
                                      </button>
                                    </div>
                                  )}
                                  {!canSignThisReport && issue.status !== 'sorted' && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Only the responsible manager can sign this off.</div>
                                  )}
                                  {!canSignThisReport && issue.status === 'sorted' && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Only the responsible manager can remove this issue.</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HealthSafetyWalkRound;